import { useCallback, useMemo } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { useArrayDataSource } from '@epam/uui-core';
import {
  DatePicker,
  Panel,
  PickerInput,
  Text,
  TextArea,
  TimePicker,
  WarningAlert,
} from '@epam/promo';

import { broadcastRoutes } from 'router/broadcastRoutes';
import {
  getTime,
  dateToString,
  timeToString,
} from 'hooks/helpers/date-time-helper';
import { useBroadcastStore } from 'store/broadcast';
import { useFeatureTogglesStore } from 'store/feature-toggling';
import { ScheduleTime } from 'models/broadcast.model';
import { BroadcastTag } from 'models/broadcastTag.model';
import { FileLoader } from 'components/recognitions/FormBody/components';
import { isBroadcastReadonly, useAppLocation } from 'hooks/helpers';
import { useGetBroadcastTagsQuery } from 'hooks/api/appSettings';
import { useUserGroupsGetQuery } from 'hooks/api/userGroups';
import { SCHEDULE_DATE_FORMAT, SYSTEM_TAG_FOR_ALL_ID } from 'common/constants';
import {
  AISuggestionWrapper,
  AISuggestionWrapperChildrenType,
  TABS,
} from '../../common/AISuggestionWrapper';
import { UserRolesPicker } from '../UserRolesPicker';
import { getSelectedUserRolesReadonlyValue } from '../UserRolesPicker/userRolesDisplay';
import css from './FormBody.module.scss';
import { InlineLabelInput } from '../../common';

const WARNING_MESSAGE_TIMEZONE = `The sending time is according to the time zone settings on your
local machine. If you are out of your location, please take into
account the time zone difference and add the necessary shift in the
set time.`;

const WARNING_MESSAGE_FOR_ALL_TAG = `"For all" tag is selected.
This broadcast will be sent to ALL users.`;

export const FormBody = () => {
  const { isCreate } = useAppLocation(broadcastRoutes);
  const {
    draft,
    errors,
    actions: {
      setDraftTitle,
      setDraftMessage,
      setDraftTagIds,
      setDraftUserRoles,
      setDraftScheduleDate,
      setDraftScheduleTime,
      setDraftPictures,
    },
  } = useBroadcastStore((state) => state);
  const { isFeatureEnabled } = useFeatureTogglesStore();
  const isBroadcastTagsEnabled = isFeatureEnabled('broadcastTags');
  const isUserGroupsRolesEnabled = isFeatureEnabled('userGroupsRoles');
  const { tags = [] } = useGetBroadcastTagsQuery({
    enabled: isBroadcastTagsEnabled,
  });
  const { data: userGroups = [] } = useUserGroupsGetQuery({
    enabled: isUserGroupsRolesEnabled,
  });

  const readonlyUserRoles = useMemo(() => {
    if (!draft.userRoles.length) return '';

    return getSelectedUserRolesReadonlyValue({
      selectedRoleIds: draft.userRoles,
      userGroups,
    });
  }, [draft.userRoles, userGroups]);

  const isReadonly = useMemo(
    () => isBroadcastReadonly(draft.status),
    [draft.status],
  );
  const schedule = useMemo(
    () => draft.schedule && new Date(draft.schedule),
    [draft.schedule],
  );

  const getScheduleDate = () => {
    if (!schedule) {
      return null;
    }

    const year = schedule.getFullYear();
    const month = schedule.getMonth() + 1;
    const day = schedule.getDate();

    return dayjs(`${year}-${month}-${day}`).format(SCHEDULE_DATE_FORMAT);
  };

  const handleTimeSelect = (newVal: ScheduleTime) => {
    setDraftScheduleTime(newVal);

    setDraftScheduleDate(
      getScheduleDate() ?? dayjs().format(SCHEDULE_DATE_FORMAT),
    );
  };

  const stringDate = useMemo(() => {
    if (!schedule) return '';

    return dateToString(schedule);
  }, [schedule]);

  const stringTime = useMemo(() => {
    if (!schedule) return '';

    return timeToString(schedule);
  }, [schedule]);

  const tagsDataSource = useArrayDataSource(
    {
      items: tags,
      getId: (item) => item.id,
    },
    [tags],
  );

  const isForAllTagSelected = useMemo(() => {
    return draft.tagIds.includes(SYSTEM_TAG_FOR_ALL_ID);
  }, [draft.tagIds]);

  const getTagRowOptions = useCallback(
    (tag: BroadcastTag) => {
      const isDisabled =
        isForAllTagSelected && tag.id !== SYSTEM_TAG_FOR_ALL_ID;

      return {
        isDisabled,
        checkbox: { isVisible: true, isDisabled },
      };
    },
    [isForAllTagSelected],
  );

  const handleTagsChange = (value: string[]) => {
    const nextTagIds = value ?? [];
    const hasForAllTag = nextTagIds.includes(SYSTEM_TAG_FOR_ALL_ID);

    if (!hasForAllTag) {
      setDraftTagIds(nextTagIds);

      return;
    }
    setDraftTagIds([SYSTEM_TAG_FOR_ALL_ID]);
  };

  const readOnlyTags = useMemo(() => {
    if (!draft.tagIds.length) {
      return '';
    }

    return draft.tagIds
      .map((id) => tags.find((tag) => tag.id === id)?.name)
      .join(', ');
  }, [draft.tagIds, tags]);

  const getMessage: AISuggestionWrapperChildrenType = ({
    onBlur,
    onFocus,
    isAISuggestionBlockShown,
  }) => (
    <div key={draft.id}>
      <TextArea
        autoSize
        inputCx={{
          [css.textarea__input]: true,
          [css['textarea__input--error']]: errors.message,
          [css['textarea__input--disabled']]: isAISuggestionBlockShown,
        }}
        isDisabled={isReadonly || isAISuggestionBlockShown}
        isInvalid={Boolean(errors.message)}
        placeholder="Add message"
        rawProps={{ 'data-testid': 'draftTask-description-input' }}
        size="42"
        value={draft.message}
        onBlur={(event) => {
          setDraftMessage(event.target.value.trim(), {
            markAsDirty: false,
            shouldValidate: true,
          });
          onBlur(event);
        }}
        onFocus={onFocus}
        onValueChange={setDraftMessage}
      />
    </div>
  );

  return (
    <Panel
      key={draft.id}
      cx={{
        [css.form]: true,
      }}>
      <InlineLabelInput
        isRequired
        isTitle
        errorText={errors.title}
        isReadonly={isReadonly}
        label="Title"
        readonlyValue={draft.title}>
        <TextArea
          autoSize
          isRequired
          autoFocus={isCreate}
          inputCx={{
            [css.textarea__input]: true,
            [css.textarea__title]: draft.title,
            [css['textarea__input--error']]: errors.title,
            [css.form__input_readonly]: isReadonly,
          }}
          isDisabled={isReadonly}
          isInvalid={Boolean(errors.title)}
          placeholder="Add title"
          rawProps={{ 'data-testid': 'title-input' }}
          size="42"
          value={draft.title}
          onBlur={({ target: { value } }) =>
            setDraftTitle(value.trim(), {
              markAsDirty: false,
              shouldValidate: true,
            })
          }
          onValueChange={setDraftTitle}
        />
      </InlineLabelInput>

      <InlineLabelInput
        isRequired
        errorText={errors.message}
        isReadonly={isReadonly}
        label="Message"
        readonlyValue={draft.message}>
        <AISuggestionWrapper
          options={{
            tab: TABS.BROADCASTS,
            field: 'message',
          }}
          setValue={setDraftMessage}
          value={draft.message}>
          {getMessage}
        </AISuggestionWrapper>
      </InlineLabelInput>

      <InlineLabelInput
        isRequired
        errorText={errors.schedule}
        isReadonly={isReadonly}
        label="Date"
        readonlyValue={stringDate}>
        <DatePicker
          filter={(day: Dayjs) =>
            day.valueOf() >= dayjs().subtract(1, 'day').valueOf()
          }
          format="MMM D, YYYY"
          inputCx={{
            [css.input]: true,
            [css.input__error]: errors.schedule,
          }}
          isDisabled={isReadonly}
          placeholder="Enter a date"
          rawProps={{ input: { 'data-testid': 'date-picker-input' } }}
          size="42"
          value={getScheduleDate()}
          onValueChange={setDraftScheduleDate}
        />
      </InlineLabelInput>

      <InlineLabelInput
        isRequired
        errorText={errors.scheduleTime}
        isReadonly={isReadonly}
        label="Time"
        readonlyValue={stringTime}>
        <TimePicker
          inputCx={{
            [css.input]: true,
            [css.input__error]: errors.schedule,
          }}
          isDisabled={isReadonly}
          placeholder="Enter a time"
          rawProps={{ input: { 'data-testid': 'time-picker-input' } }}
          size="42"
          value={schedule ? getTime(schedule, draft.scheduleTime) : null}
          onValueChange={(newVal) => {
            if (newVal) {
              handleTimeSelect(newVal);
            }
          }}
        />
      </InlineLabelInput>

      <InlineLabelInput>
        <WarningAlert cx={css['form__warning-alert']}>
          <Text fontSize="14" size="none">
            {WARNING_MESSAGE_TIMEZONE}
          </Text>
        </WarningAlert>
      </InlineLabelInput>

      <InlineLabelInput align="top" label="Your files">
        <FileLoader
          attachments={draft.attachments}
          isReadonly={isReadonly}
          setDraftPictures={setDraftPictures}
        />
      </InlineLabelInput>

      {isUserGroupsRolesEnabled && (
        <InlineLabelInput
          align="top"
          isReadonly={isReadonly}
          label="User Roles"
          readonlyValue={readonlyUserRoles}>
          <UserRolesPicker
            isDisabled={isReadonly}
            value={draft.userRoles}
            onChange={setDraftUserRoles}
          />
        </InlineLabelInput>
      )}

      {isBroadcastTagsEnabled && (
        <>
          <InlineLabelInput
            isRequired
            errorText={errors.tagIds}
            isReadonly={isReadonly}
            label="Tags"
            readonlyValue={readOnlyTags}>
            <PickerInput
              dataSource={tagsDataSource}
              getName={(item) => item?.name ?? ''}
              getRowOptions={getTagRowOptions}
              inputCx={{
                [css.input]: true,
                [css.input__error]: errors.tagIds,
                [css.input__with_icons]: true,
              }}
              isDisabled={isReadonly}
              placeholder="Select broadcast tags"
              selectionMode="multi"
              value={draft.tagIds}
              valueType="id"
              onValueChange={handleTagsChange}
            />
          </InlineLabelInput>

          {isForAllTagSelected && (
            <InlineLabelInput>
              <WarningAlert cx={css['form__warning-alert']}>
                <Text fontSize="14" size="none">
                  {WARNING_MESSAGE_FOR_ALL_TAG}
                </Text>
              </WarningAlert>
            </InlineLabelInput>
          )}
        </>
      )}
    </Panel>
  );
};
